// Evidence/source panel — list linked evidence and link new items from any module.
import { useEffect, useState } from "react";
import { Plus, X, Link2, Trash2, Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const TYPE_LABELS = {
    golden_thread: "Golden Thread", document: "Document", fire: "Fire Safety", bsa: "BSA Register",
    inspection: "Inspection", site_visit: "Site Visit", incident: "Incident", action: "Action", upload: "Manual note",
};

export default function EvidencePanel({ rid, code, scenarioId, evidence = [], canEdit, onChange, compact }) {
    const [adding, setAdding] = useState(false);

    async function remove(evid) {
        try {
            await api.delete(`/bsc/${rid}/section/${code || "W"}/evidence/${evid}`);
            toast.success("Evidence unlinked"); onChange?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    }

    return (
        <div data-testid="bscr-evidence-panel">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] flex items-center gap-1.5">
                    <Paperclip size={12} /> Evidence ({evidence.length})
                </h4>
                {canEdit && (
                    <button data-testid="bscr-link-evidence-btn" onClick={() => setAdding(true)}
                        className="inline-flex items-center gap-1 text-[11.5px] font-semibold" style={{ color: "var(--brand-accent,#C75A1B)" }}>
                        <Plus size={12} /> Link
                    </button>
                )}
            </div>
            {evidence.length === 0 ? (
                <p className="text-[11.5px] italic text-[#9A968F]">{compact ? "No evidence." : "No evidence linked yet. Missing evidence reduces completeness."}</p>
            ) : (
                <ul className="space-y-1.5">
                    {evidence.map((e) => (
                        <li key={e.id} data-testid={`bscr-evidence-${e.id}`} className="flex items-start gap-2 rounded-lg border border-[#EFEAE4] bg-[#FCFBF9] px-2.5 py-1.5">
                            <Link2 size={12} className="mt-0.5 text-[#9A968F] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="text-[12px] text-[#1F1F1F] truncate">{e.label}</div>
                                <div className="text-[10px] uppercase tracking-wide text-[#9A968F]">{TYPE_LABELS[e.source_type] || e.source_type}</div>
                            </div>
                            {canEdit && <button onClick={() => remove(e.id)} className="text-[#C0BBB3] hover:text-[#B33A2E]"><Trash2 size={12} /></button>}
                        </li>
                    ))}
                </ul>
            )}
            {adding && (
                <LinkDialog rid={rid} code={code} scenarioId={scenarioId} onClose={() => setAdding(false)}
                    onLinked={() => { setAdding(false); onChange?.(); }} />
            )}
        </div>
    );
}

function LinkDialog({ rid, code, scenarioId, onClose, onLinked }) {
    const [type, setType] = useState("golden_thread");
    const [options, setOptions] = useState(null);
    const [selected, setSelected] = useState("");
    const [manualLabel, setManualLabel] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (type === "upload") { setOptions([]); return; }
        setOptions(null); setSelected("");
        api.get(`/bsc/${rid}/evidence-options`, { params: { type } })
            .then((r) => setOptions(r.data)).catch(() => setOptions([]));
    }, [type, rid]);

    async function link() {
        let label = manualLabel, sourceId = null;
        if (type !== "upload") {
            const opt = (options || []).find((o) => o.id === selected);
            if (!opt) { toast.error("Select an item"); return; }
            label = opt.label; sourceId = opt.id;
        } else if (!manualLabel.trim()) { toast.error("Enter a note"); return; }
        setBusy(true);
        try {
            await api.post(`/bsc/${rid}/section/${code || "W"}/evidence`, {
                source_type: type, source_id: sourceId, label, scenario_id: scenarioId || null,
            });
            toast.success("Evidence linked"); onLinked();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4" style={{ background: "rgba(26,26,26,.45)" }} onClick={onClose}>
            <div className="bg-white rounded-[14px] w-full max-w-[460px] p-5 shadow-brand-lg" onClick={(e) => e.stopPropagation()} data-testid="bscr-link-dialog">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-[15px] font-bold text-[#1F1F1F]">Link evidence</h3>
                    <button onClick={onClose} className="text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">Source</label>
                <select data-testid="bscr-evidence-type" value={type} onChange={(e) => setType(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-[#E5E0DA] text-[13px] mb-3">
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                {type === "upload" ? (
                    <input data-testid="bscr-evidence-note" placeholder="Reference / note" value={manualLabel}
                        onChange={(e) => setManualLabel(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-[#E5E0DA] text-[13px]" />
                ) : options == null ? (
                    <div className="text-center py-4 text-[#9A968F]"><Loader2 size={14} className="inline animate-spin" /></div>
                ) : options.length === 0 ? (
                    <p className="text-[12px] italic text-[#9A968F] py-2">No items available in this module for this building.</p>
                ) : (
                    <select data-testid="bscr-evidence-option" value={selected} onChange={(e) => setSelected(e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-[#E5E0DA] text-[13px]">
                        <option value="">Select an item…</option>
                        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                )}
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-3.5 py-2 rounded-lg border border-[#E5E0DA] text-[12.5px] font-semibold">Cancel</button>
                    <button data-testid="bscr-evidence-save" onClick={link} disabled={busy}
                        className="px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-white inline-flex items-center gap-1.5" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} Link
                    </button>
                </div>
            </div>
        </div>
    );
}
